import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { FiX } from 'react-icons/fi'

interface AddPeanutLotModalProps {
  isOpen: boolean
  onClose: () => void
  onLotAdded?: (lot: any) => void
}

const inputStyle = {
  width: '100%',
  height: '44px',
  borderRadius: '8px',
  paddingLeft: '12px',
  paddingRight: '12px',
  paddingTop: '12px',
  paddingBottom: '12px',
  backgroundColor: '#FFFFFF',
  border: '1px solid #E9E9EA',
  fontFamily: 'Lexend Deca',
  fontStyle: 'normal' as const,
  fontSize: '12px',
  lineHeight: '20px',
  letterSpacing: '0px',
}

const labelStyle = {
  fontFamily: 'Lexend Deca',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: '#212126',
  display: 'block' as const,
  marginBottom: '8px',
}

const INITIAL_FORM = {
  buyContractNumber: '',
  saleContractNumber: '',
  vesselVoyage: '',
  booking: '',
  billOfLading: '',
  eta: '',
  ata: '',
  containerNumber: '',
  batchNumber: '',
  containerDischargeDate: '',
  containerGateIn: '',
  containerGateOut: '',
  unpackDateTime: '',
  daysInWarehouse: '',
  location: '',
  marksAndNumbers: '',
  product: '',
  barcode: '',
  pieces: '',
  itemNumber: '',
  weight: '',
  dispatchNumber: '',
  pickingListNumber: '',
  bags: '',
  fclLcl: '',
  dispatchDate: '',
  status: '',
}

const AddPeanutLotModal = ({ isOpen, onClose, onLotAdded }: AddPeanutLotModalProps) => {
  const [formData, setFormData] = useState({ ...INITIAL_FORM })
  const [labelFile, setLabelFile] = useState<File | null>(null)
  const [technicalSheetFile, setTechnicalSheetFile] = useState<File | null>(null)

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      document.body.style.overflow = 'hidden'

      return () => {
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        document.body.style.overflow = ''
        window.scrollTo(0, scrollY)
      }
    }
  }, [isOpen])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleFileChange = (field: 'label' | 'technicalSheet') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    if (field === 'label') {
      setLabelFile(file)
    } else {
      setTechnicalSheetFile(file)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (onLotAdded) {
      const payload: Record<string, any> = {}
      for (const [k, v] of Object.entries(formData)) {
        if (v !== '') payload[k] = v
      }
      if (labelFile) payload.labelFile = labelFile
      if (technicalSheetFile) payload.technicalSheetFile = technicalSheetFile
      onLotAdded(payload)
    }
    setFormData({ ...INITIAL_FORM })
    setLabelFile(null)
    setTechnicalSheetFile(null)
    onClose()
  }

  if (!isOpen) return null

  const field = (label: string, name: keyof typeof formData, type: 'text' | 'date' | 'number' = 'text', placeholder?: string) => (
    <div className="flex flex-col">
      <label style={labelStyle}>{label}</label>
      <input
        type={type}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        placeholder={placeholder ?? `Enter ${label.toLowerCase()}`}
        className="w-full border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        style={{
          ...inputStyle,
          fontWeight: formData[name] ? 400 : 300,
          color: formData[name] ? '#212126' : '#69697A',
        }}
      />
    </div>
  )

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end"
      onClick={onClose}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      style={{ zIndex: 80, overflow: 'hidden' }}
    >
      <div
        className="bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          width: '640px',
          maxWidth: '640px',
          height: '100vh',
          zIndex: 90,
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 style={{ fontFamily: 'Lexend Deca', fontWeight: 600, fontSize: '20px', lineHeight: '28px', color: '#212126' }}>
            Add Product Batch
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded transition-colors" style={{ color: '#69697A' }}>
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form id="add-product-batch-form" onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-x-4 gap-y-5">
              {field('Buy Contract Number', 'buyContractNumber')}
              {field('Sale Contract Number', 'saleContractNumber')}
              {field('Vessel / Voyage', 'vesselVoyage')}
              {field('Booking', 'booking')}
              {field('Bill of Lading', 'billOfLading')}
              {field('ETA', 'eta', 'date')}
              {field('ATA', 'ata', 'date')}
              {field('Container Number', 'containerNumber')}
              {field('Batch Number', 'batchNumber')}
              {field('Container Discharge Date', 'containerDischargeDate', 'date')}
              {field('Container Gate In', 'containerGateIn', 'date')}
              {field('Container Gate Out', 'containerGateOut', 'date')}
              {field('Unpack Date Time', 'unpackDateTime', 'date')}
              {field('Days in Warehouse', 'daysInWarehouse', 'number')}
              {field('Location', 'location')}
              {field('Marks And Numbers', 'marksAndNumbers')}
              {field('Product', 'product')}
              {field('Barcode', 'barcode')}
              {field('Pieces', 'pieces', 'number')}
              {field('Item Number', 'itemNumber')}
              {field('Weight', 'weight', 'number')}
              {field('Dispatch Number', 'dispatchNumber')}
              {field('Picking List Number', 'pickingListNumber')}
              {field('Bags', 'bags', 'number')}
              {field('FCL / LCL', 'fclLcl', 'text', 'e.g. FCL')}
              {field('Dispatch Date', 'dispatchDate', 'date')}

              {/* Status */}
              <div className="flex flex-col">
                <label style={labelStyle}>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full border appearance-none rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{
                    ...inputStyle,
                    paddingRight: '32px',
                    fontWeight: formData.status ? 400 : 300,
                    color: formData.status ? '#212126' : '#69697A',
                  }}
                >
                  <option value="">Select status</option>
                  <option value="In Warehouse">In Warehouse</option>
                  <option value="Dispatched">Dispatched</option>
                  <option value="In Transit">In Transit</option>
                  <option value="Delivered">Delivered</option>
                </select>
              </div>

              {/* Label upload */}
              <div className="flex flex-col">
                <label style={labelStyle}>Label</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                  onChange={handleFileChange('label')}
                  className="w-full border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{
                    ...inputStyle,
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    height: 'auto',
                  }}
                />
                {labelFile && (
                  <span style={{ marginTop: '4px', fontFamily: 'Lexend Deca', fontSize: '11px', color: '#69697A' }}>
                    Selected: {labelFile.name}
                  </span>
                )}
              </div>

              {/* Technical Sheet upload */}
              <div className="flex flex-col">
                <label style={labelStyle}>Technical Sheet</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
                  onChange={handleFileChange('technicalSheet')}
                  className="w-full border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  style={{
                    ...inputStyle,
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    height: 'auto',
                  }}
                />
                {technicalSheetFile && (
                  <span style={{ marginTop: '4px', fontFamily: 'Lexend Deca', fontSize: '11px', color: '#69697A' }}>
                    Selected: {technicalSheetFile.name}
                  </span>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
            style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#212126', border: '1px solid #E9E9EA', backgroundColor: '#FFFFFF' }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="add-product-batch-form"
            className="px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
            style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#FFFFFF', backgroundColor: '#212126' }}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default AddPeanutLotModal
